import * as amplitude from '@amplitude/analytics-browser';
import { getProteinTargetG } from "./quiz/protein";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStatsigClient } from "@statsig/react-bindings";
import { CheckoutProvider, useCheckout } from "@stripe/react-stripe-js/checkout";
import { loadStripe } from "@stripe/stripe-js";
import { nanoid } from "nanoid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useChartCalculations } from "./charts/utils";
import { AfterTrialPeriod } from "./features/checkout/AfterTrialPeriod";
import { CheckoutForm } from "./features/checkout/CheckoutForm";
import trophyImage from "./features/checkout/images/trophy.svg";
import { CDN_ROOT } from "./features/images";
import { useCheckoutPlan } from "./hooks/useCheckoutPlan";
import { createGetClientSecretPromise, getCheckoutOfferMetadata, getWeb46DiscountArmForAnnualCoupon, getWeb46DiscountPercent, Web46DiscountArm } from "./hooks/useCreactStripeClientSecret";
import { CheckoutPlan, getTransformationData } from "./LandingPage";
import { Age } from "./quiz/age";
import { track } from "./services/facebookPixel";
import { User } from "./types";
import { getAssignedExperimentArm, getInternalCoreEndpoint, getWeb30V3Arm, getWeb35Arm, getWeb45Arm, getWeb46Arm, getWelcomeKitAssignment, WEB30_V3_EXPERIMENT_NAME, WEB45_EXPERIMENT_NAME } from "./utils";
import { WEB29_OFFER_CHECKOUT_GRACE_MS, getWeb29OfferState, toAnchorCheckoutMode, Web29AnchorCheckoutMode } from "./web29Offer";
import { Model } from "survey-core";
import { shouldUseStripeTestMode } from "./checkoutTestMode";
import { WelcomeKitSummary } from "./features/welcomeKit/WelcomeKitSummary";
import { isWelcomeKitPlan } from "./features/welcomeKit/welcomeKit";
import { readMatchOfferState } from "./features/web46/matchOffer";
import { getCheckoutReferralCode, isReferralAnnualPlan, useReferral } from "./referral";
import { getStoredShareId } from "./shareLink";
import { ReferralPlanNote } from "./features/ReferralBanner";
import { LanguageSwitch } from "./i18n/LanguageSwitch";
import { appEntryLanguageOverride } from "./i18n/language";
import {
  APP_CHECKOUT_DEFAULT_PLAN_ID,
  isAppCheckoutEntry,
  readAppCheckoutDiscountId,
  readAppCheckoutReturnParams,
} from "./appCheckoutEntry";

// web34_checkout_cta: Statsig experiment key (arm param: control | variation).
const WEB34_EXPERIMENT_NAME = "web34_checkout_cta";

const STRIPE_TEST_PUBLISHABLE_KEY =
    "pk_test_51OKsGtJnrTlLWSIiRvG09Ew7FlApAv7H73wWpchZ3wZ6w1pWQsyhGBk13ky0kG910Ek89M8sjeLqMxTlybW8M20u002KVRvAmt";

function getStripePublishableKey(search: string) {
    if (shouldUseStripeTestMode(search)) {
        return STRIPE_TEST_PUBLISHABLE_KEY;
    }

    return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || STRIPE_TEST_PUBLISHABLE_KEY;
}

type CheckoutPageProps = {
    user: User;
    survey: Model;
    onComplete: () => void;
};

type CheckoutPageInnerProps = CheckoutPageProps & {
    referralCode?: string;
    onReinitializeCheckout: (newClientSecret: string) => void;
    plan: CheckoutPlan;
    isNoTrialVariation: boolean;
    // web29 anchor arm while its offer is active: the pricing page showed a
    // 20% anchor, so mirror the applied-coupon chip DISPLAY-ONLY (there is no
    // coupon — the charge is control's price on control's price id).
    isUrgencyAnchorOffer?: boolean;
    urgencyAnchorMode?: Web29AnchorCheckoutMode | null;
    // web35_no_trial_urgency variation: which offer state the pay-today
    // checkout was created in ("offer" / "email" / "expired"); null otherwise.
    web35Mode?: Web29AnchorCheckoutMode | null;
    // web30_v3_checkout_momentum variation: the momentum line replaces the
    // "Complete Your Checkout" heading above the payment options.
    isWeb30V3Variation?: boolean;
    // web40_welcome_kit variation (paid-only): annual checkout includes the kit.
    welcomeKitOn?: boolean;
    // Any welcome-kit variation (v1 or v2): the annual session carries the
    // offer id so Core tags kit buyers, even when the checkout UI is control.
    welcomeKitOffer?: boolean;
    // web34_checkout_cta 'variation' arm: final submit button copy
    // "Try for $0.00" (trial checkouts only; see CreditCardForm).
    isWeb34Variation?: boolean;
    // web45_referral_code variation: "Have a referral code?" on the payment
    // heading opens the code field above the payment buttons.
    isWeb45Variation?: boolean;
    // web46_match_offer: a real MATCH40/SPECIAL40/SPECIAL30 coupon is on this
    // session.
    isWeb46DiscountOffer?: boolean;
    web46DiscountPercent?: number;
    promoCode: string;
    setPromoCode: (code: string) => void;
    isPromoApplied: boolean;
    setIsPromoApplied: (applied: boolean) => void;
    promoError: string;
    setPromoError: (error: string) => void;
};

const Wrapper = styled.div`
    background-color: #f6f6f8;
    min-height: 100vh;
    width: 100%;
`;

// Off-state keeps the original container; the kit variation constrains it.
// Spanish quiz: English / Español switch (renders nothing for English-only visitors).
const CheckoutLanguageSwitch = styled(LanguageSwitch)`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
`;

const CheckoutPageContainer = styled.div<{ $kit?: boolean }>`
    ${({ $kit }) => ($kit ? 'box-sizing: border-box; width: 100%;' : '')}
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    min-height: 100vh;

    @media (max-width: 768px) {
        padding: 10px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        margin-bottom: 20px;
    }
`;

const Logo = styled.div`
    display: flex;
    align-items: center;
    color: #406edf;
    font-weight: bold;
    font-size: 24px;

    @media (max-width: 768px) {
        font-size: 20px;
    }
`;

const MainContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const LeftPanel = styled.div`
    display: flex;
    flex-direction: column;
`;

const RightPanel = styled.div`
    display: flex;
    flex-direction: column;
`;

const PersonalPlanCard = styled.div`
    background: white;
    border-radius: 16px;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        margin-bottom: 0px;
    }
`;

const PersonalPlanCardInner = styled.div`
    padding: 24px;
`;

const PlanTitle = styled.h1`
    font-size: 24px;
    font-weight: bold;
    color: #1e1e1e;
    margin-bottom: 0;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const TargetGoal = styled.div`
    font-size: 32px;
    font-weight: 800;
    color: #406edf;
    margin-bottom: 25px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: 768px) {
        font-size: 28px;
        margin-bottom: 20px;
    }
`;

const ExpandButton = styled.button`
    background: none;
    border: none;
    color: #406edf;
    cursor: pointer;
    padding: 5px;

    @media (max-width: 768px) {
        display: block;
    }
`;

const Timeline = styled.div`
    margin-bottom: 25px;
    position: relative;

    @media (max-width: 768px) {
        margin-bottom: 20px;
        padding: 0 16px;
    }
`;

const TimelineContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;

    @media (min-width: 769px) {
        flex-direction: row;
        align-items: center;
        gap: 0;
        min-height: 80px;
        position: relative;
    }
`;

const TimelinePoints = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0px;

    @media (min-width: 769px) {
        flex-direction: row;
        gap: 0;
        height: 100%;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        gap: 15px;
    }
`;

const TimelinePoint = styled.div<{ isActive?: boolean; isTarget?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;

    @media (min-width: 769px) {
        flex: 0 0 auto;
        position: relative;
        z-index: 3;
        flex-direction: column;
    }
`;

const DotWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    @media (min-width: 769px) {
        width: 48px;
        height: 48px;
    }
`;

const TimelineFirstDot = styled.div`
    box-sizing: border-box;
    border: 2px solid white;
    background-color: #0b6dff;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.15));

    @media (min-width: 769px) {
        width: 32px;
        height: 32px;
    }
`;

const TimelineMiddleDot = styled.div`
    box-sizing: border-box;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(138.01deg, #ffffff 10.71%, #e7f0ff 95.77%);
    border: 2px solid rgba(11, 109, 255, 0.5);

    @media (min-width: 769px) {
        width: 28px;
        height: 28px;
    }
`;

const TimelineLastDot = styled.div`
    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: #0b6dff;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
        width: 75%;
    }
    @media (min-width: 769px) {
        width: 48px;
        height: 48px;
    }
`;

const TimelineLine = styled.div`
    background: #f6f6f8;
    border-radius: 173.303px;

    width: 8px;
    height: 24px;

    @media (max-width: 768px) {
        transform: translateX(14px);
    }

    @media (min-width: 768px) {
        height: 8px;
        width: auto;
        flex: 1;
        transform: translateY(-29px);
    }
`;

const TimelineLabel = styled.div<{ isActive?: boolean }>`
    color: ${(props) => (props.isActive ? "#1A1A1A" : "#7A7A7A")};
    text-align: center;
    margin-bottom: 4px;

    font-family: "Oswald";
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 140%;
    margin-left: 16px;

    @media (min-width: 769px) {
        font-size: 28px;
        margin-left: 0;
    }
`;

const TimelineValue = styled.div<{ isNow?: boolean; isTarget?: boolean }>`
    font-family: "Lato";
    font-style: normal;
    line-height: 140%;
    margin-left: auto;

    font-weight: ${(props) => (props.isTarget ? "700" : "500")};
    font-size: ${(props) => (props.isTarget ? "20px" : "16px")};
    color: ${(props) =>
        props.isNow ? "#000" : props.isTarget ? "#0B6DFF" : "#7A7A7A"};

    @media (min-width: 769px) {
        color: ${(props) => (props.isTarget ? "#0B6DFF" : "#7A7A7A")};
        margin-left: 0;
    }
`;

const PlanFeatures = styled.div`
    margin-bottom: 30px;
    margin-top: 28px;

    @media (max-width: 769px) {
        border-top: 1px solid #d9d9d9;
    }

    @media (min-width: 769px) {
        margin-top: 80px;
    }
`;

const SeparatorContainer = styled.div`
    display: none;
    padding: 0 26px;
    margin-bottom: 44px;

    @media (min-width: 769px) {
        display: block;
    }
`;

const Separator = styled.div`
    border-top: 1px solid #d9d9d9;
`;

const FeaturesTitle = styled.h3`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 115%;
    color: #000000;

    @media (min-width: 768px) {
        font-size: 24px;
    }
`;

const FeatureItem = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;

    @media (max-width: 768px) {
        margin-bottom: 12px;
    }
`;

const FeatureIcon = styled.div`
    width: 20px;
    height: 20px;
    background: #406edf;
    border-radius: 50%;
    margin-right: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 10px;
`;

const FeatureText = styled.span`
    color: #1e1e1e;
    font-size: 16px;

    @media (min-width: 768px) {
        font-size: 20px;
    }
`;

const DesktopTrustIndicatorsContainer = styled.div`
    padding: 32px 0 49px;
    border-radius: 0 0 10px 10px;
`;

const MobileTrustIndicatorsContainer = styled.div`
    margin-top: 24px;
    margin-bottom: 8px;

    @media (min-width: 768px) {
        display: none;
    }
`;

const TrustIndicators = styled.div`
    display: flex;
    gap: 20px;
    margin-top: auto;

    @media (max-width: 768px) {
        gap: 15px;
    }
`;

const TrustItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;

    @media (max-width: 768px) {
        background-color: #fff;
        padding: 16px 8px;
        border-radius: 10px;
    }
`;

const TrustIcon = styled.div``;

const TrustText = styled.span`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 12px;
    margin-top: 8px;

    color: #000000;

    @media (min-width: 768px) {
        font-size: 14.4px;
    }
`;

const OrderSummaryCard = styled.div`
    box-sizing: border-box;
    background: #ffffff;
    /* card shadow */
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    padding: 16px;

    @media (min-width: 768px) {
        padding: 25px;
    }
`;

const OrderSummaryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const OrderSummaryTitle = styled.h3`
    font-size: 16px;
    font-weight: bold;
    color: #1e1e1e;
    margin: 0;

    @media (min-width: 768px) {
        font-size: 24px;
    }
`;

const CheckoutReferralRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: -4px 0 4px;
    padding: 10px 14px;
    border-radius: 10px;
    background: #e3f5ea;
    color: #317a40;
    font: 700 14px/1.35 'Lato', sans-serif;
`;

const CheckoutReferralIcon = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
`;

const FreeTrialBadge = styled.div`
    background: #047c2b;
    border-radius: 5px;
    color: white;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: bold;

    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const PriceRow = styled.div<{ white?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background-color: ${(props) => (props.white ? "#fff" : "transparent")};
    border-radius: 10px;
    padding: 16px 0;
`;

// Left label/subtext column — shrink + wrap so a long subtext never collides
// with the price on narrow screens.
const PriceLabelGroup = styled.div`
    flex: 1;
    min-width: 0;
`;

const PriceSeparator = styled.div`
    border-bottom: 1px solid #e0e0e0;
    margin-top: 24px;

    @media (min-width: 768px) {
        margin-top: 16px;
    }
`;

const PriceLabel = styled.span`
    color: #1e1e1e;
    font-size: 16px;

    @media (min-width: 768px) {
        font-size: 20px;
    }
`;

const PriceValue = styled.span<{ isHighlight?: boolean }>`
    font-family: "Lato";
    font-style: normal;
    font-weight: 700;
    font-size: 32px;
    line-height: 115%;
    color: #047c2b;

    font-size: ${(props) => (props.isHighlight ? "24px" : "20px")};
    color: ${(props) => (props.isHighlight ? "#047C2B" : "#000")};

    @media (min-width: 768px) {
        font-size: ${(props) => (props.isHighlight ? "32px" : "20px")};
    }
`;

const PriceCluster = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
`;

const PriceSubtext = styled.span`
    margin-top: 5px;
    display: block;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 115%;
    text-align: left;
    color: #7a7a7a;
`;

const ReminderBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 13px 16px;
    gap: 12px;

    background: #d7e7fe;
    border-radius: 10px;
`;

const ReminderIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
`;

const ReminderText = styled.span`
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 140%;
    color: #000000;

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const PromoCodeContainer = styled.div`
    margin-top: 16px;
    position: relative;
`;

const PromoCodeInput = styled.input<{ disabled?: boolean; hasError?: boolean }>`
    width: 100%;
    padding: 13px 90px 13px 16px;
    border: 1px solid ${(props) => (props.hasError ? "#ff4444" : "#e0e0e0")};
    border-radius: 10px;
    font-size: 14px;
    box-sizing: border-box;
    background-color: ${(props) => (props.disabled ? "#f6f6f8" : "#ffffff")};
    
    &:focus {
        outline: none;
        border-color: ${(props) => (props.hasError ? "#ff4444" : "#406edf")};
    }

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const PromoCodeError = styled.div`
    color: #ff4444;
    font-size: 12px;
    margin-top: 8px;
    
    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const PromoCodeButton = styled.button<{ isApplied?: boolean }>`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: ${(props) => (props.isApplied ? "#ff4444" : "#406edf")};
    font-weight: 600;
    font-size: 14px;
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${(props) => 
            props.isApplied ? "rgba(255, 68, 68, 0.1)" : "rgba(64, 110, 223, 0.1)"};
    }

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

const PromoCodeSpinner = styled.div`
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #406edf;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% { transform: translateY(-50%) rotate(0deg); }
        100% { transform: translateY(-50%) rotate(360deg); }
    }
`;

const Spinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #406edf;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% { transform: translateY(-50%) rotate(0deg); }
        100% { transform: translateY(-50%) rotate(360deg); }
    }
`;

const PaymentSection = styled.div`
    margin-bottom: 25px;
`;

// web30_v3_checkout_momentum: replaces the "Complete Your Checkout" heading
// in the variation — one line, centered, second half in the brand blue, per
// the doc's mock.
const MomentumLine = styled.h3`
    font-style: normal;
    font-weight: 700;
    font-size: 18px;
    line-height: 140%;
    text-align: center;
    color: #1A1A1A;
    margin: 24px 0 16px 0;

    span {
        color: #0B6DFF;
    }

    @media (min-width: 768px) {
        font-size: 20px;
    }
`;

const PaymentTitle = styled.h3`
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 140%;
    color: #000000;
    margin: 24px 0 16px 0;

    @media (min-width: 768px) {
        font-size: 24px;
        margin: 36px 0 24px 0;
    }
`;

const PaymentButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const TermsText = styled.p`
    text-align: center;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 115%;
    color: #000000;

    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const MobileHeader = styled.div<{ isExpanded: boolean }>`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;
        border-radius: 12px;
    }
`;

const MobileHeaderContent = styled.div<{ isExpanded: boolean }>`
    display: ${(props) => (props.isExpanded ? "block" : "none")};
    animation: ${(props) => (props.isExpanded ? "slideDown" : "slideUp")} 0.3s
        ease;
    width: 100%;

    @keyframes slideDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    @keyframes slideUp {
        from {
            opacity: 1;
            transform: translateY(0);
        }
        to {
            opacity: 0;
            transform: translateY(-10px);
        }
    }
`;

const StripeText = styled.p`
    text-align: center;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 115%;
    color: #000000;
`;

// web45: the code field sits directly above the payment buttons. 16 px text
// keeps iOS Safari from zooming the page when the field takes focus.
const ReferralCodeContainer = styled(PromoCodeContainer)`
    margin-top: 0;
`;

const ReferralCodeInput = styled(PromoCodeInput)`
    font-size: 16px;
`;

const ActivePromotionBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 10px 14px;
    background-color: #f2f4f7;
    color: #5f6368;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;

    @media (min-width: 768px) {
        font-size: 16px;
    }
`;

// web45_referral_code: a low-key text link in the promotion chip's grey, and the
// applied code as a chip with Remove.
const ReferralLink = styled.button`
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    font: 400 13px/16px "Lato", sans-serif;
    color: #5f6368;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
    white-space: nowrap;
    @media (min-width: 768px) {
        font-size: 14px;
    }
`;

const PaymentHeadingRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    & > h3 {
        flex: 1;
        min-width: 0;
    }
`;

const ReferralLinkRow = styled.div`
    text-align: right;
    margin: -8px 0 16px;
`;

const ReferralField = styled.div`
    margin-bottom: 15px;
`;

const ReferralRemoveButton = styled.button`
    margin-left: auto;
    background: none;
    border: 0;
    padding: 0;
    color: #406edf;
    font: 600 13px "Lato", sans-serif;
    cursor: pointer;
`;

const ReferralAppliedBadge = styled(ActivePromotionBadge)<{ $tight?: boolean }>`
    margin-top: ${(props) => (props.$tight ? "8px" : "16px")};
`;

const ActivePromotionIcon = styled.span`
    display: flex;
    align-items: center;
    font-size: 16px;
`;


const Checkmark = () => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="24" height="24" rx="11.6463" fill="#0B6DFF" />
            <path
                d="M9.8916 17.3715L4.78906 12.269L6.0647 10.9933L9.8916 14.8202L18.1049 6.60693L19.3805 7.88257L9.8916 17.3715Z"
                fill="white"
            />
        </svg>
    );
};

const CheckoutPageInner: React.FC<CheckoutPageInnerProps> = ({
    referralCode,
    user,
    plan,
    isNoTrialVariation,
    isUrgencyAnchorOffer = false,
    web35Mode = null,
    isWeb30V3Variation = false,
    welcomeKitOn = false,
    welcomeKitOffer = false,
    isWeb34Variation = false,
    isWeb45Variation = false,
    isWeb46DiscountOffer = false,
    web46DiscountPercent,
    onReinitializeCheckout,
    promoCode,
    setPromoCode,
    isPromoApplied,
    setIsPromoApplied,
    promoError,
    setPromoError,
    urgencyAnchorMode = null
}) => {
    const { t, i18n } = useTranslation();
    const { referral } = useReferral();
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isPromoAppliedLoading, setIsPromoAppliedLoading] = useState(false);
    const [isReferralOpen, setIsReferralOpen] = useState(false);

    const checkout = useCheckout();
    console.log("checkout", promoCode, isPromoApplied, checkout);
    const { titlePrediction, titlePredictionDate, timeToGoal } =
        useChartCalculations(user);
    const transformationData = getTransformationData(
        user,
        timeToGoal,
        CDN_ROOT
    );

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);

        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const handlePromoCodeButton = async () => {
        if (checkout.type !== "success") {
            return;
        }

        if (isPromoApplied) {
            // Remove code - reinitialize without promo code
            try {
                setIsPromoAppliedLoading(true);
                const result =await checkout.checkout.removePromotionCode();
                if (result.type === "success") {
                    setPromoError("");
                    setIsPromoApplied(false);
                    setPromoCode("");
                    setPromoError("");
                    setIsReferralOpen(false);
                } else {
                    setPromoError(t("checkout.promo.removeFailed"));
                }
            } catch (error) {
                console.log("error", error);
                setPromoError(t("checkout.promo.genericError"));
            } finally {
                setIsPromoAppliedLoading(false);
            }
        } else {
            // Apply code
            if (promoCode.trim()) {
                // web45: every attempt logs the code typed, normalized, so
                // code use (and abuse) is visible per code.
                const submittedCode = promoCode.trim().toUpperCase();
                // NOTRIAL20A/NOTRIAL20M were the earlier pay-today path's
                // auto-applied codes (web29 toggle arms, retired). They still
                // exist in Stripe, so keep them out of the hand-entered box.
                const RESERVED_NO_TRIAL_CODES = ["NOTRIAL20A", "NOTRIAL20M"];
                if (RESERVED_NO_TRIAL_CODES.includes(promoCode.trim().toUpperCase())) {
                    setPromoError(t("checkout.promo.notValidForTrial"));
                    setIsPromoApplied(false);
                    if (isWeb45Variation) amplitude.track("ReferralCodeSubmitted", { result: "blocked", code: submittedCode });
                    return;
                }
                // web45: the baseline offer's SPECIAL20 chip is display-only, so the
                // real SPECIAL20/SUMMER20 coupons must not be typed in on top of it.
                if (isWeb45Variation && /^(SPECIAL-?20|SUMMER20)/.test(promoCode.trim().toUpperCase())) {
                    setPromoError(t("checkout.promo.codeNotValid"));
                    setIsPromoApplied(false);
                    amplitude.track("ReferralCodeSubmitted", { result: "blocked", code: submittedCode });
                    return;
                }
                try {
                    setIsPromoAppliedLoading(true);
                    setPromoError("");
                    
                    const response = await fetch(
                        `${getInternalCoreEndpoint()}/internal/public/checkout/custom-apply-promo`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                promotionCode: promoCode.trim(),
                                // Keep the in-app return URL, recovery linkage and Spanish
                                // completion page on the re-created session.
                                ...(readAppCheckoutReturnParams(window.location.search) ?? {}),
                                ...(readAppCheckoutReturnParams(window.location.search) && i18n.language === "es"
                                    ? { lang: "es" }
                                    : {}),
                                subscriptionPriceId: plan.priceId,
                                unlockProductId: plan.unlockProductId,
                                trialProductId: plan.trialProductId,
                                referralCode: getCheckoutReferralCode(plan, !!referralCode),
                                shareId: getStoredShareId(),
                                ...getCheckoutOfferMetadata(plan, isNoTrialVariation, urgencyAnchorMode, welcomeKitOffer),
                                prefillEmail: user.email?.trim() || undefined,
                                // web42: keep the protein target on the re-created session.
                                protein_target_g: getProteinTargetG(user) ? String(getProteinTargetG(user)) : undefined,
                            }),
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();
                        if (data.data.client_secret) {
                            onReinitializeCheckout(data.data.client_secret);
                            setIsPromoApplied(true);
                            setPromoError("");
                            if (isWeb45Variation) amplitude.track("ReferralCodeSubmitted", { result: "applied", code: submittedCode });
                        }
                    } else {
                        // 4xx error means invalid promo code
                        const errorData = await response.json().catch(() => ({}));
                        console.log("Invalid promo code", response.status, errorData);
                        // Core's error message is English; Spanish visitors get the generic line.
                        setPromoError(
                            isWeb45Variation
                                ? t("checkout.promo.codeNotValid")
                                : (i18n.language !== "es" && errorData.message) || t("checkout.promo.invalid")
                        );
                        setIsPromoApplied(false);
                        if (isWeb45Variation) amplitude.track("ReferralCodeSubmitted", { result: "invalid", code: submittedCode });
                    }
                } catch (error) {
                    console.log("error", error);
                    setPromoError(t("checkout.promo.genericError"));
                    setIsPromoApplied(false);
                    if (isWeb45Variation) amplitude.track("ReferralCodeSubmitted", { result: "error", code: submittedCode });
                } finally {
                    setIsPromoAppliedLoading(false);
                }
            }
        }
    };

    const timeline = (
        <Timeline>
            <TimelineContainer>
                <TimelinePoints>
                    {transformationData.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <TimelinePoint>
                                <DotWrapper>
                                    {index === 0 && <TimelineFirstDot />}
                                    {index ===
                                        transformationData.length - 1 && (
                                        <TimelineLastDot>
                                            <img
                                                src={trophyImage}
                                                alt={t("checkout.plan.trophyAlt")}
                                            />
                                        </TimelineLastDot>
                                    )}
                                    {index !== 0 &&
                                        index !==
                                            transformationData.length - 1 && (
                                            <TimelineMiddleDot />
                                        )}
                                </DotWrapper>
                                <TimelineLabel isActive>
                                    {item.timelineLabel}
                                </TimelineLabel>
                                <TimelineValue isNow>
                                    {item.labelbodyfat}
                                </TimelineValue>
                            </TimelinePoint>
                            {index !== transformationData.length - 1 && (
                                <TimelineLine />
                            )}
                        </React.Fragment>
                    ))}
                </TimelinePoints>
            </TimelineContainer>
        </Timeline>
    );

    const infoBoxAndPlanFeatures = (
        <>
            <PlanFeatures>
                <FeaturesTitle>{t("checkout.plan.featuresTitle")}</FeaturesTitle>
                <FeatureItem>
                    <FeatureIcon>
                        <Checkmark />
                    </FeatureIcon>
                    <FeatureText>
                        {t("checkout.plan.featureAdaptive")}
                    </FeatureText>
                </FeatureItem>
                <FeatureItem>
                    <FeatureIcon>
                        <Checkmark />
                    </FeatureIcon>
                    <FeatureText>
                        {t("checkout.plan.featureCoaching")}
                    </FeatureText>
                </FeatureItem>
                <FeatureItem>
                    <FeatureIcon>
                        <Checkmark />
                    </FeatureIcon>
                    <FeatureText>
                        {t("checkout.plan.featureTools")}
                    </FeatureText>
                </FeatureItem>
                {welcomeKitOn && isWelcomeKitPlan(plan) && (
                    <FeatureItem>
                        <FeatureIcon><Checkmark /></FeatureIcon>
                        <FeatureText>Welcome kit</FeatureText>
                    </FeatureItem>
                )}
            </PlanFeatures>
        </>
    );

    const trustIndicators = (
        <>
            <SeparatorContainer>
                <Separator />
            </SeparatorContainer>
            <TrustIndicators>
                <TrustItem>
                    <TrustIcon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="19"
                            height="21"
                            viewBox="0 0 19 21"
                            fill="none"
                        >
                            <path
                                d="M6.375 9.4585L8.4585 11.5415L12.625 7.375"
                                stroke="#0B6DFF"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M9.49804 19.875L10.4145 19.479C12.3362 18.6544 13.9956 17.3197 15.213 15.6195C16.4304 13.9193 17.1594 11.9184 17.321 9.8335L17.769 4.0835C17.7763 3.84537 17.7017 3.61196 17.5575 3.42229C17.4133 3.23262 17.2084 3.0982 16.977 3.0415L9.49804 1.125L2.01904 3C1.78773 3.05668 1.58288 3.19103 1.43873 3.3806C1.29458 3.57016 1.21985 3.80346 1.22704 4.0415L1.67504 9.7915C1.83658 11.8765 2.56554 13.8775 3.78295 15.5778C5.00036 17.2781 6.65981 18.6128 8.58154 19.4375L9.49804 19.875Z"
                                stroke="#0B6DFF"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </TrustIcon>
                    <TrustText>{t("checkout.trust.securePayment")}</TrustText>
                </TrustItem>
                <TrustItem>
                    <TrustIcon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="25"
                            viewBox="0 0 24 25"
                            fill="none"
                        >
                            <path
                                d="M18.838 8.375C18.7007 10.2814 17.2864 11.75 15.7443 11.75C14.2021 11.75 12.7855 10.2819 12.6505 8.375C12.5099 6.39172 13.8866 5 15.7443 5C17.6019 5 18.9786 6.42781 18.838 8.375Z"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M15.7498 14.75C12.695 14.75 9.75731 16.2673 9.02138 19.2223C8.92388 19.6133 9.16903 20 9.57075 20H21.9293C22.3311 20 22.5748 19.6133 22.4787 19.2223C21.7428 16.22 18.8051 14.75 15.7498 14.75Z"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-miterlimit="10"
                            />
                            <path
                                d="M9.36715 9.21594C9.25746 10.7384 8.11465 11.9375 6.88277 11.9375C5.6509 11.9375 4.50621 10.7389 4.3984 9.21594C4.28637 7.63203 5.39871 6.5 6.88277 6.5C8.36683 6.5 9.47918 7.66109 9.36715 9.21594Z"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M9.65671 14.8434C8.81061 14.4557 7.87874 14.3066 6.89108 14.3066C4.45358 14.3066 2.10515 15.5184 1.51686 17.8785C1.43952 18.1907 1.63546 18.4996 1.95608 18.4996H7.21921"
                                stroke="#0B6DFF"
                                stroke-width="1.3125"
                                stroke-miterlimit="10"
                                stroke-linecap="round"
                            />
                        </svg>
                    </TrustIcon>
                    <TrustText>{t("checkout.trust.members")}</TrustText>
                </TrustItem>
                <TrustItem>
                    <TrustIcon>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                        >
                            <path
                                d="M15.4994 10.5002L11.4994 14.5002L9.49937 12.5002M13.7454 3.95919L14.9664 4.99919C15.2744 5.26119 15.6564 5.41919 16.0584 5.45219L17.6584 5.58019C18.1131 5.61651 18.5401 5.81357 18.8628 6.13607C19.1855 6.45858 19.3828 6.88544 19.4194 7.34019L19.5464 8.94019C19.5794 9.34319 19.7384 9.72619 20.0004 10.0332L21.0404 11.2532C21.3368 11.6007 21.4996 12.0425 21.4996 12.4992C21.4996 12.9559 21.3368 13.3977 21.0404 13.7452L20.0004 14.9662C19.7384 15.2742 19.5794 15.6562 19.5474 16.0592L19.4194 17.6592C19.3831 18.114 19.186 18.5409 18.8635 18.8636C18.541 19.1863 18.1141 19.3836 17.6594 19.4202L16.0594 19.5482C15.6565 19.5801 15.2741 19.7383 14.9664 20.0002L13.7454 21.0402C13.3979 21.3366 12.9561 21.4994 12.4994 21.4994C12.0426 21.4994 11.6009 21.3366 11.2534 21.0402L10.0334 20.0002C9.7254 19.7381 9.34253 19.5799 8.93937 19.5482L7.33937 19.4202C6.88446 19.3836 6.45746 19.1861 6.13494 18.8633C5.81241 18.5404 5.61545 18.1131 5.57937 17.6582L5.45137 16.0592C5.41898 15.6566 5.26046 15.2745 4.99837 14.9672L3.95837 13.7452C3.6625 13.3978 3.5 12.9565 3.5 12.5002C3.5 12.0439 3.6625 11.6025 3.95837 11.2552L4.99837 10.0332C5.26137 9.72519 5.41837 9.34319 5.45037 8.94019L5.57837 7.34119C5.61477 6.88595 5.81226 6.45859 6.13538 6.13584C6.4585 5.81309 6.88608 5.61609 7.34137 5.58019L8.93937 5.45319C9.3423 5.42101 9.7248 5.26248 10.0324 5.00019L11.2534 3.96019C11.6009 3.66379 12.0426 3.50098 12.4994 3.50098C12.9561 3.50098 13.3979 3.66279 13.7454 3.95919Z"
                                stroke="#0B6DFF"
                                stroke-width="1.66667"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </TrustIcon>
                    <TrustText>{isNoTrialVariation ? "30-day Money-Back Guarantee" : t("checkout.trust.freeTrial")}</TrustText>
                </TrustItem>
            </TrustIndicators>
        </>
    );

    const discount = checkout.type === "success" ? checkout.checkout.discountAmounts?.[0] : null;
    // need a better way to check for active promotion
    const appliedDiscountName = discount && discount.displayName;
    // web29 urgency-offer: the gym-buddy/family coupons are Stripe objects from
    // the summer sale (display names SUMMER20FAMILY*/SUMMER20GYMBUDDY*). Show
    // them as SPECIAL20 on the chip so the offer branding is consistent; the
    // underlying coupon/charge is unchanged.
    const displayDiscountName =
        appliedDiscountName && appliedDiscountName.toLocaleUpperCase().startsWith("SUMMER20")
            ? "SPECIAL20"
            : appliedDiscountName;
    const promotionDiscountNames = [
        "STUDENTANNUAL",
        "STUDENTMONTHLY",
        "STUDENT-ANNUAL",
        "STUDENT-MONTHLY",
        // web29 urgency-offer arm: SPECIAL20 (annual) / SPECIAL-20 (monthly),
        // plus the gym-buddy/family coupon display names (quiz4's U55dUFLX /
        // IcSZrw1w family) so the applied-coupon chip renders on family checkout.
        "SPECIAL20",
        "SPECIAL-20",
        "SUMMER20MONTHLY",
        "SUMMER20ANNUAL",
        "SUMMER20GYMBUDDYMONTHLY",
        "SUMMER20GYMBUDDYANNUAL",
        "SUMMER20FAMILYMONTHLY",
        "SUMMER20FAMILYANNUAL",
        "MATCH40",
        "SPECIAL40",
        "SPECIAL30",
    ];
    const hasActivePromotion =
        promotionDiscountNames.includes(appliedDiscountName?.toLocaleUpperCase() ?? "");
    // Baseline offer active: display-only SPECIAL20 chip, promo input hidden.
    // Nothing is sent to Stripe — see isUrgencyAnchorOffer.
    const showDisplayOnlyPromotion = isUrgencyAnchorOffer && !isWeb46DiscountOffer && !hasActivePromotion;

    // No-trial (web35) checkout charges the first period today.
    const getDueTodayAmount = () => {
        if (checkout.type !== "success") return null;
        const item = checkout.checkout.lineItems.find((it: any) => !!it.unitAmount);
        if (!item) return null;
        const pct = item.discountAmounts?.[0]?.percentOff ?? 0;
        let amt = item.unitAmount.minorUnitsAmount / 100;
        if (pct > 0) amt = Math.floor(amt * (1 - pct / 100) * 100) / 100;
        return `$${amt.toFixed(2)}`;
    };

    // web29 weekly price: the no-trial Due-Today row leads with the weekly
    // equivalent of today's discounted charge (annual/52, monthly×12/52 — the
    // same math AfterTrialPeriod uses for the trial row), so the paid state
    // mirrors the trial row's "$X.XX/week" hierarchy. The billed-today total
    // moves to the small subtext underneath.
    const getWeeklyEquivalent = () => {
        if (checkout.type !== "success") return null;
        const item = checkout.checkout.lineItems.find((it: any) => !!it.unitAmount);
        if (!item) return null;
        const pct = item.discountAmounts?.[0]?.percentOff ?? 0;
        let amt = item.unitAmount.minorUnitsAmount / 100;
        if (pct > 0) amt = Math.floor(amt * (1 - pct / 100) * 100) / 100;
        const interval = item.recurring?.interval || "year";
        const weekly = interval === "month" ? (amt * 12) / 52 : amt / 52;
        return `$${weekly.toFixed(2)}/week`;
    };

    // The app's offer coupon (?discountId=) is on this session.
    const isAppDiscountOffer = !!readAppCheckoutDiscountId(window.location.search, plan);

    // web45_referral_code: the link sits on the payment heading until it is
    // tapped or a code is applied.
    const showReferralLink = isWeb45Variation && !isWeb46DiscountOffer && !isReferralOpen && !isPromoApplied && checkout.type === "success";
    const referralLink = (
        <ReferralLink
            type="button"
            onClick={() => {
                setIsReferralOpen(true);
                amplitude.track("ReferralCodeOpened");
            }}
        >
            {t("checkout.referralCode.link")}
        </ReferralLink>
    );

    return (
        <Wrapper>
            <CheckoutPageContainer $kit={welcomeKitOn}>
                <CheckoutLanguageSwitch />
                <MainContent>
                    <LeftPanel>
                        <PersonalPlanCard>
                            <PersonalPlanCardInner>
                                <PlanTitle>
                                    {user?.name
                                        ? t("checkout.plan.titleWithName", { name: user.name })
                                        : ` ${t("checkout.plan.title")}`}
                                </PlanTitle>

                                <TargetGoal>
                                    {titlePrediction} {titlePredictionDate}
                                    <ExpandButton
                                        onClick={() =>
                                            setIsHeaderExpanded(
                                                !isHeaderExpanded
                                            )
                                        }
                                        style={{
                                            display: isMobile
                                                ? "block"
                                                : "none",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                isHeaderExpanded
                                                    ? faChevronUp
                                                    : faChevronDown
                                            }
                                        />
                                    </ExpandButton>
                                </TargetGoal>

                                {isHeaderExpanded && (
                                    <MobileHeader isExpanded={isHeaderExpanded}>
                                        <MobileHeaderContent
                                            isExpanded={isHeaderExpanded}
                                        >
                                            {timeline}
                                            {infoBoxAndPlanFeatures}
                                        </MobileHeaderContent>
                                    </MobileHeader>
                                )}

                                {!isMobile && (
                                    <>
                                        {timeline}
                                        {infoBoxAndPlanFeatures}
                                    </>
                                )}
                            </PersonalPlanCardInner>
                            {!isMobile && (
                                <DesktopTrustIndicatorsContainer>
                                    {trustIndicators}
                                </DesktopTrustIndicatorsContainer>
                            )}
                        </PersonalPlanCard>
                    </LeftPanel>

                    <RightPanel>
                        <OrderSummaryCard>
                            <OrderSummaryHeader>
                                <OrderSummaryTitle>
                                    {t("checkout.summary.title")}
                                </OrderSummaryTitle>
                                <FreeTrialBadge>{isNoTrialVariation ? "30-DAY GUARANTEE" : t("checkout.summary.freeTrialBadge")}</FreeTrialBadge>
                            </OrderSummaryHeader>

                            {referralCode && referral && (
                                <CheckoutReferralRow role="status" data-testid="checkout-referral-row">
                                    <CheckoutReferralIcon aria-hidden="true">🎁</CheckoutReferralIcon>
                                    <span>{t("checkout.summary.referralUnlocked", { name: referral.referrerFirstName })}</span>
                                </CheckoutReferralRow>
                            )}

                            <NonAnnualReferralNote plan={plan} />

                            {welcomeKitOn && isWelcomeKitPlan(plan) && (
                                <WelcomeKitSummary name={user.name} isTrial={!isNoTrialVariation} />
                            )}

                            <PriceRow white>
                                <PriceLabelGroup>
                                    <PriceLabel>{isNoTrialVariation ? "Your price" : t("checkout.summary.dueToday")}</PriceLabel>
                                    <PriceSubtext>
                                        {isNoTrialVariation
                                            ? (web35Mode === "expired" ? "Refundable for 30 days." : "20% off discount applied")
                                            : t("checkout.summary.noPaymentToday")}
                                    </PriceSubtext>
                                </PriceLabelGroup>
                                <PriceCluster>
                                    {isNoTrialVariation ? (
                                        // web29 weekly price: weekly figure leads (trial-row
                                        // hierarchy); the actual charge sits underneath.
                                        <>
                                            <PriceValue>{getWeeklyEquivalent() ?? "—"}</PriceValue>
                                            <PriceSubtext>
                                                {getDueTodayAmount()
                                                    ? `${getDueTodayAmount()} billed today`
                                                    : "billed today"}
                                            </PriceSubtext>
                                        </>
                                    ) : (
                                        <PriceValue isHighlight>$0.00</PriceValue>
                                    )}
                                </PriceCluster>
                            </PriceRow>
                            {checkout.type === "success" && (
                                <>
                                    {!isNoTrialVariation && (
                                        <>
                                            <PriceSeparator />
                                            <PriceRow>
                                                <AfterTrialPeriod
                                                    user={user}
                                                    showDiscountText={isWeb46DiscountOffer || isAppDiscountOffer || (isWeb45Variation && isPromoApplied)}
                                                    discountLabelPercent={isWeb46DiscountOffer ? web46DiscountPercent : undefined}
                                                    hasReferral={!!referralCode}
                                                />
                                            </PriceRow>
                                        </>
                                    )}

                                    <ReminderBox>
                                        <ReminderIcon>{isNoTrialVariation ? "🛡️" : "🔔"}</ReminderIcon>
                                        <ReminderText>
                                            {isNoTrialVariation
                                                ? "Risk-free 30-day money-back guarantee. No questions asked."
                                                : t("checkout.summary.trialReminder")}
                                        </ReminderText>
                                    </ReminderBox>

                                    {hasActivePromotion && appliedDiscountName && !(isWeb45Variation && isPromoApplied) && (
                                        <ActivePromotionBadge>
                                            <ActivePromotionIcon>🏷️</ActivePromotionIcon>
                                            {displayDiscountName}
                                        </ActivePromotionBadge>
                                    )}
                                    {showDisplayOnlyPromotion && (
                                        <ActivePromotionBadge>
                                            <ActivePromotionIcon>🏷️</ActivePromotionIcon>
                                            SPECIAL20
                                        </ActivePromotionBadge>
                                    )}
                                    {isWeb45Variation && isPromoApplied && (
                                        <ReferralAppliedBadge $tight={showDisplayOnlyPromotion} data-testid="referral-code-applied">
                                            <ActivePromotionIcon>🏷️</ActivePromotionIcon>
                                            {promoCode.trim().toUpperCase()}
                                            <ReferralRemoveButton
                                                type="button"
                                                onClick={handlePromoCodeButton}
                                                disabled={isPromoAppliedLoading}
                                            >
                                                {t("checkout.referralCode.remove")}
                                            </ReferralRemoveButton>
                                        </ReferralAppliedBadge>
                                    )}
                                    {!isWeb46DiscountOffer && !isWeb45Variation && !hasActivePromotion && !showDisplayOnlyPromotion && (
                                        <>
                                            <PromoCodeContainer>
                                                <PromoCodeInput
                                                    type="text"
                                                    placeholder={t("checkout.promo.placeholder")}
                                                    value={promoCode}
                                                    onChange={(e) => {
                                                        setPromoCode(e.target.value);
                                                        setPromoError("");
                                                    }}
                                                    disabled={isPromoApplied || isPromoAppliedLoading}
                                                    hasError={!!promoError}
                                                />
                                                {isPromoAppliedLoading ? (
                                                    <PromoCodeSpinner />
                                                ) : (
                                                    <PromoCodeButton
                                                        isApplied={isPromoApplied}
                                                        onClick={handlePromoCodeButton}
                                                        type="button"
                                                        disabled={checkout.type !== "success"}
                                                    >
                                                        {isPromoApplied ? t("checkout.promo.removeCode") : t("checkout.promo.apply")}
                                                    </PromoCodeButton>
                                                )}
                                            </PromoCodeContainer>
                                            {promoError && <PromoCodeError>{promoError}</PromoCodeError>}
                                        </>
                                    )}
                                </>
                            )}
                            {checkout.type === "loading" && (
                              <PriceRow white>
                                <Spinner />
                              </PriceRow>
                            )}
                        </OrderSummaryCard>

                        <PaymentSection>
                            {isWeb30V3Variation ? (
                                <>
                                    <MomentumLine>
                                        <Trans i18nKey="checkout.payment.momentumLine" components={{ 1: <span /> }} />
                                    </MomentumLine>
                                    {showReferralLink && <ReferralLinkRow>{referralLink}</ReferralLinkRow>}
                                </>
                            ) : showReferralLink ? (
                                <PaymentHeadingRow>
                                    <PaymentTitle>{t("checkout.payment.title")}</PaymentTitle>
                                    {referralLink}
                                </PaymentHeadingRow>
                            ) : (
                                <PaymentTitle>{t("checkout.payment.title")}</PaymentTitle>
                            )}
                            {isWeb45Variation && !isWeb46DiscountOffer && isReferralOpen && !isPromoApplied && (
                                <ReferralField>
                                    <ReferralCodeContainer>
                                        <ReferralCodeInput
                                            type="text"
                                            placeholder={t("checkout.referralCode.placeholder")}
                                            value={promoCode}
                                            autoFocus
                                            autoCapitalize="characters"
                                            autoComplete="off"
                                            spellCheck={false}
                                            onChange={(e) => {
                                                setPromoCode(e.target.value);
                                                setPromoError("");
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handlePromoCodeButton();
                                                }
                                            }}
                                            disabled={isPromoAppliedLoading}
                                            hasError={!!promoError}
                                        />
                                        {isPromoAppliedLoading ? (
                                            <PromoCodeSpinner />
                                        ) : (
                                            <PromoCodeButton
                                                type="button"
                                                onClick={handlePromoCodeButton}
                                                disabled={checkout.type !== "success"}
                                            >
                                                {t("checkout.promo.apply")}
                                            </PromoCodeButton>
                                        )}
                                    </ReferralCodeContainer>
                                    {promoError && <PromoCodeError>{promoError}</PromoCodeError>}
                                </ReferralField>
                            )}
                            <PaymentButtons>
                                <CheckoutForm isWeb34Variation={isWeb34Variation} />
                            </PaymentButtons>
                        </PaymentSection>

                        {isMobile && (
                            <MobileTrustIndicatorsContainer>
                                {trustIndicators}
                            </MobileTrustIndicatorsContainer>
                        )}

                        <TermsText>
                            {isNoTrialVariation
                                ? `By completing your purchase, you agree to our Terms of Service and Privacy Policy. You'll be charged today${web35Mode === "expired" ? "" : " with 20% off"} and can request a full refund within 30 days. Your plan renews at the same price until you cancel.`
                                : t("checkout.terms.trial")}
                        </TermsText>
                        {isNoTrialVariation && (
                            <TermsText>Questions? Email support@builtwithscience.com.</TermsText>
                        )}
                        <StripeText>{t("checkout.poweredByStripe")}</StripeText>
                    </RightPanel>
                </MainContent>
            </CheckoutPageContainer>
        </Wrapper>
    );
};

export const CheckoutPage: React.FC<CheckoutPageProps> = (props) => {
    const { t, i18n } = useTranslation();
    // Spanish visitors get Stripe's Spanish UI and error messages. "es-419" is
    // Stripe's Latin American Spanish ("es" is Spain's: "móvil", "caducidad"),
    // matching the quiz's neutral Latin American copy. English keeps today's
    // call (no locale option: Stripe's default "auto"), except the app entry's
    // explicit ?lang=en, which pins Stripe to English so a Spanish device does
    // not get Spanish Stripe buttons on an English page.
    const stripeLocale = i18n.language === "es" ? "es-419" : appEntryLanguageOverride() === "en" ? "en" : undefined;
    // The app's checkout entry never saw a pricing page and always gets control
    // pricing and the control checkout, whatever paid-ad UTM state, timer,
    // stored referral or experiment assignment this browser carries from
    // earlier visits.
    const isAppEntry = isAppCheckoutEntry(window.location.search);
    const referralState = useReferral();
    const referral = isAppEntry ? null : referralState.referral;
    const referralPending = isAppEntry ? false : referralState.pending;
    const { client } = useStatsigClient();
    // web35_no_trial_urgency: the variation is a pay-today checkout on the
    // baseline offer pricing; everything else about the offer state is shared.
    // Match pricing's referral offer: the standard trial, without enrolling
    // this visit in incompatible pay-today or teen-discount offers.
    const web35Arm = isAppEntry || referral || referralPending ? undefined : getWeb35Arm(client);
    const isWeb35NoTrial = web35Arm === 'variation';
    // web30_v3_checkout_momentum (organic only): the arms first differ on this
    // page, so the exposure is logged here, once per mount, both arms.
    const web30V3Arm = isAppEntry ? undefined : getWeb30V3Arm(client);
    const isWeb30V3Variation = web30V3Arm === 'variation';
    const web30V3ExposedRef = useRef(false);
    useEffect(() => {
        if (!web30V3Arm || web30V3ExposedRef.current) return;
        web30V3ExposedRef.current = true;
        amplitude.track("ExperimentStarted", {
            experimentName: WEB30_V3_EXPERIMENT_NAME,
            variantName: web30V3Arm,
        });
    }, [web30V3Arm]);
    // web45_referral_code: all checkouts. The arms first differ on this page,
    // so the exposure is logged here, once per mount, both arms. Referral
    // visits already carry a code: no web45 enrollment, no "Have a referral code?".
    const web45Arm = isAppEntry || referral || referralPending ? undefined : getWeb45Arm(client);
    const isWeb45Variation = web45Arm === 'variation';
    const web45ExposedRef = useRef(false);
    useEffect(() => {
        if (!web45Arm || web45ExposedRef.current) return;
        web45ExposedRef.current = true;
        amplitude.track("ExperimentStarted", {
            experimentName: WEB45_EXPERIMENT_NAME,
            variantName: web45Arm,
        });
    }, [web45Arm]);
    // Welcome kit (web40 paid / web40_welcome_kit_organic): exposure is logged on the
    // pricing page; here only the arm matters.
    const welcomeKit = isAppEntry ? undefined : getWelcomeKitAssignment(client);
    const welcomeKitOffer = welcomeKit?.arm === 'variation';
    // v2 (shelf below the pricing CTA) keeps the CONTROL checkout UI; only the
    // v1 layout shows the kit summary + feature line here.
    const welcomeKitOn = welcomeKitOffer && welcomeKit?.layout === 'v1';
    // Site baseline offer state (formerly web29's urgency-anchor arm), read
    // ONCE when this page mounts: control's price ids while the offer is
    // live/revived, the regular (÷0.8) price ids once expired — plus the
    // Stripe pricing_variant tag. No coupon in any mode. The Stripe session
    // created at mount keeps its price if the clock runs out while the user
    // is here; a reload or new visit after expiry gets the regular price —
    // with a 10-second grace so the page load itself can't cost someone the
    // offer.
    // The app's checkout entry has no baseline offer state: control's price,
    // no pricing_variant tag.
    const urgencyAnchorMode = useMemo<Web29AnchorCheckoutMode | null>(
        () => isAppEntry ? null : toAnchorCheckoutMode(getWeb29OfferState(Date.now(), WEB29_OFFER_CHECKOUT_GRACE_MS)),
        [isAppEntry]
    );
    const navigate = useNavigate();

    const web17_3Experiment = referral || referralPending ? undefined : client?.getExperiment("web17_3");
    const web17_3Arm = web17_3Experiment?.value["arm"];
    const isTeen = props.user.age === Age.Teens;
    const isWeb17_3Variation =
        // The app's checkout entry always gets control pricing.
        !isAppEntry &&
        isTeen &&
        web17_3Arm === 'variation';


    console.log("isWeb17_3Variation", isWeb17_3Variation);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    // No trial: the web35 variation, or ?trial=false (QA). Pay today on the
    // plan's own price, no coupon, tagged pricing_variant=no_trial.
    const isNoTrialVariation = isWeb35NoTrial || searchParams.get("trial") === "false";
    // Referral visitors keep the standard trial offer: no web46 enrollment.
    // The app entry is never enrolled either; its offer coupon (?discountId=,
    // single-user annual or monthly) selects the matching web46 discount arm
    // so the page shows that arm's anchor pricing.
    const appDiscountId = readAppCheckoutDiscountId(location.search, {
        id: searchParams.get("plan") ?? APP_CHECKOUT_DEFAULT_PLAN_ID,
        isMultiUser: searchParams.get("isMultiUser") === "true",
    });
    const web46Arm = isAppEntry
        ? getWeb46DiscountArmForAnnualCoupon(appDiscountId)
        : referral || referralPending ? undefined : getWeb46Arm(client);
    const hasClaimedMatchOffer = useMemo(
        () => readMatchOfferState()?.claimedAt !== undefined,
        []
    );
    const eligibleWeb46DiscountArm: Web46DiscountArm | undefined =
        web46Arm === "discount" || web46Arm === "discount30"
            ? web46Arm
            : web46Arm === "personalization" && hasClaimedMatchOffer
                ? "personalization"
                : undefined;
    // The web46 coupon is only valid for active/revived trial checkouts. Keep
    // the existing no-trial and teen web17_3 routes unchanged.
    const web46DiscountArm =
        urgencyAnchorMode !== "expired" && !isNoTrialVariation && !isWeb17_3Variation
            ? eligibleWeb46DiscountArm
            : undefined;
    const isWeb46DiscountOffer = !!web46DiscountArm;
    const web46DiscountPercent = web46DiscountArm ? getWeb46DiscountPercent(web46DiscountArm) : undefined;
    const plan = useCheckoutPlan(urgencyAnchorMode, web46DiscountPercent);
    const referralCode = referral && !referralPending
        ? getCheckoutReferralCode(plan, !isNoTrialVariation && !(isWeb17_3Variation && plan?.experiments?.web17_3?.discount))
        : undefined;
    // web34_checkout_cta: copy-only test on the final submit button ("Start
    // trial" vs "Try for $0.00"). Randomizes independently of web29. Only
    // TRIAL checkouts are enrolled: the no-trial (pay-today) path skips the
    // getExperiment call entirely — no Statsig exposure, no ExperimentStarted
    // event, control copy — because "$0.00" would be false there.
    const web34Arm =
        client && !isNoTrialVariation
            ? getAssignedExperimentArm(WEB34_EXPERIMENT_NAME, client)
            : undefined;
    const isWeb34Variation = web34Arm === 'variation';
    const web34ExposedRef = useRef(false);
    useEffect(() => {
        if (isNoTrialVariation || !client || web34ExposedRef.current) return;
        web34ExposedRef.current = true;
        amplitude.track("ExperimentStarted", {
            experimentName: WEB34_EXPERIMENT_NAME,
            variantName: web34Arm ?? "control",
        });
    }, [client, isNoTrialVariation, web34Arm]);
    const stripePublishableKey = useMemo(
        () => getStripePublishableKey(location.search),
        [location.search]
    );
    const stripePromise = useMemo(
        () => (stripeLocale ? loadStripe(stripePublishableKey, { locale: stripeLocale }) : loadStripe(stripePublishableKey)),
        [stripePublishableKey, stripeLocale]
    );

    const eventID = useMemo(() => searchParams.get("fb_event_id") || "", [searchParams]);

    const [checkoutKey, setCheckoutKey] = useState(0);
    const [clientSecret, setClientSecret] = useState<{ secret: string; referralCode?: string } | null>(null);
    
    const [promoCode, setPromoCode] = useState("");
    const [isPromoApplied, setIsPromoApplied] = useState(false);
    const [promoError, setPromoError] = useState<string>("");

    const fetchClientSecret = useMemo(() => {
        // If we have a custom client secret (from promo code), use it
        if (clientSecret && clientSecret.referralCode === referralCode) {
            console.log("using client secret from state", clientSecret);
            return () => Promise.resolve(clientSecret.secret);
        }
        // Otherwise use the default function
        console.log("using default client secret function");
        return createGetClientSecretPromise(plan, eventID, props.user, isWeb17_3Variation, isNoTrialVariation, urgencyAnchorMode, welcomeKitOffer, web46DiscountArm);
    }, [plan, clientSecret, eventID, props.user, isWeb17_3Variation, isNoTrialVariation, urgencyAnchorMode, welcomeKitOffer, web46DiscountArm, referralCode]);

    const handleReinitializeCheckout = (newClientSecret: string) => {
        setClientSecret({ secret: newClientSecret, referralCode });
        setCheckoutKey(prev => prev + 1); // Force remount
    };

    useEffect(() => {
      if (!props.user || !props.user.focus) {
        navigate("/");
    }
    }, [props.user, navigate]);

    useEffect(() => {
        track("InitiateCheckout", undefined, { eventID });
        amplitude.track("CheckoutPageViewed", { planId: plan?.id });
    }, [plan, eventID]);
   

    if (!plan) {
        return null;
    }

    // Do not create an unattributed session while a landing code is in flight.
    if (referralPending) return <p role="status">{t("checkout.loading")}</p>;

    return (
        <CheckoutProvider
            // The stripe prop cannot change on a mounted provider, so a language
            // switch remounts it (English keeps the old key).
            key={`${checkoutKey}:${referralCode || ""}${stripeLocale ? `:${stripeLocale}` : ""}`}
            stripe={stripePromise}
            options={{
                fetchClientSecret,
                elementsOptions: { appearance: { theme: "stripe" } },
            }}
        >
            <CheckoutPageInner
                {...props}
                referralCode={referralCode}
                plan={plan}
                isNoTrialVariation={isNoTrialVariation}
                isUrgencyAnchorOffer={!isWeb46DiscountOffer && (urgencyAnchorMode === "offer" || urgencyAnchorMode === "email")}
                urgencyAnchorMode={urgencyAnchorMode}
                web35Mode={isNoTrialVariation ? urgencyAnchorMode : null}
                isWeb30V3Variation={isWeb30V3Variation}
                welcomeKitOn={welcomeKitOn}
                welcomeKitOffer={welcomeKitOffer}
                isWeb34Variation={isWeb34Variation}
                isWeb45Variation={isWeb45Variation}
                isWeb46DiscountOffer={isWeb46DiscountOffer}
                web46DiscountPercent={web46DiscountPercent}
                onReinitializeCheckout={handleReinitializeCheckout}
                promoCode={promoCode}
                setPromoCode={setPromoCode}
                isPromoApplied={isPromoApplied}
                setIsPromoApplied={setIsPromoApplied}
                promoError={promoError}
                setPromoError={setPromoError}
            />

        </CheckoutProvider>
    );
};

export default CheckoutPage;

function NonAnnualReferralNote({ plan }: { plan: CheckoutPlan }) {
    const { referral } = useReferral();
    const { t } = useTranslation();
    return referral && !isReferralAnnualPlan(plan)
        ? <ReferralPlanNote>{t("checkout.summary.referralAnnualOnly")}</ReferralPlanNote>
        : null;
}
